import { Disc3 } from "lucide-react";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { cn } from "@web/lib/utils.ts";

/**
 * Playlist list thumb. Uses `coverUrl` today (Spotify image or first track cover).
 * Pass `coverUrls` later for a 2×2 mosaic — when ≥4 tiles are present we render the
 * grid; until then we keep falling back to the single primary cover / Disc icon.
 */
export function PlaylistThumb({
  coverUrl,
  coverUrls,
  alt,
  className,
}: {
  coverUrl: string | null | undefined;
  coverUrls?: readonly string[] | null;
  alt: string;
  className?: string;
}) {
  const tiles = (coverUrls ?? []).filter((u): u is string => Boolean(u)).slice(0, 4);
  if (tiles.length >= 4) {
    return (
      <div
        className={cn(
          "relative grid size-6 shrink-0 grid-cols-2 grid-rows-2 overflow-hidden rounded-sm bg-panel-elevated",
          className,
        )}
        aria-label={alt}
      >
        {tiles.map((src) => (
          <img
            key={src}
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
          />
        ))}
        <div className="pointer-events-none absolute inset-0 shadow-inset-cover" aria-hidden />
      </div>
    );
  }

  const src = coverUrl ?? tiles[0] ?? null;
  if (src) {
    return <EntityAvatar src={src} alt={alt} size="xs" rounded="sm" className={className} />;
  }

  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-sm bg-panel-elevated text-muted",
        className,
      )}
      aria-hidden
    >
      <Disc3 className="size-3.5" />
    </span>
  );
}
